import { Link } from "react-router-dom";
import { ArrowUpRight, Gift, Home, PartyPopper, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";
import { useContent } from "@/content/ContentContext";
import { site } from "@/config/site";

const serviceKeys = ["regalo", "domicilio", "sala"];
const icons = { regalo: Gift, domicilio: Home, sala: PartyPopper };

const waFor = (text) =>
  `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(text)}`;

const ServiceCard = ({ id, index, data, image, labels, detailed }) => {
  const Icon = icons[id];
  return (
    <article
      data-testid={`service-card-${id}`}
      className="group flex flex-col bg-ivory border border-sand/70"
    >
      <div className="frame overflow-hidden">
        <img
          src={image}
          data-edit-img={`servizi.${id}`}
          alt={data.alt}
          loading="lazy"
          className="w-full aspect-[16/11] object-cover"
        />
      </div>
      <div className="flex-1 flex flex-col p-7 md:p-9">
        <div className="flex items-center gap-4">
          <span className="font-serif italic text-xl text-sand">
            {String(index + 1).padStart(2, "0")}
          </span>
          <Icon className="w-4 h-4 text-bordeaux" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-[0.24em] text-anthracite/50">
            {data.kicker}
          </span>
        </div>
        <h3 className="mt-5 font-serif text-2xl md:text-3xl leading-tight tracking-tight">
          {data.name}
        </h3>
        <p className="mt-4 text-sm md:text-base font-light leading-relaxed text-anthracite/70">
          {data.desc}
        </p>
        {detailed && (
          <p className="mt-5 pt-5 border-t border-sand/70 text-sm font-light italic leading-relaxed text-anthracite/55">
            {data.detail}
          </p>
        )}
        <div className="mt-auto pt-8 flex flex-wrap items-center gap-3">
          <a
            href={waFor(`Ciao U Magazin! Vorrei informazioni su: ${data.name}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`service-wa-${id}`}
            className="inline-flex items-center gap-2 bg-anthracite text-ivory px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-bordeaux transition-colors duration-300"
          >
            {labels.ask}
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </a>
          {detailed && (
            <a
              href={site.phoneHref}
              data-testid={`service-call-${id}`}
              className="inline-flex items-center gap-2 border border-anthracite/25 px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:border-bordeaux hover:text-bordeaux transition-colors duration-300"
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
              {labels.call}
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

const ServiziSection = ({ detailed = false, withHeading = true }) => {
  const { t } = useLang();
  const s = t.servizi;
  const images = useContent().images.servizi;

  return (
    <section
      id="servizi"
      className="bg-sand/20 border-y border-sand/50"
      data-testid="servizi-section"
    >
      <div
        className={`max-w-7xl mx-auto px-5 md:px-10 pb-24 md:pb-36 ${
          withHeading ? "pt-24 md:pt-36" : "pt-14 md:pt-20"
        }`}
      >
        {withHeading && (
          <Reveal>
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end">
              <div className="md:col-span-7">
                <p className="eyebrow text-olive mb-7">{s.label}</p>
                <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tight">
                  {s.title}
                </h2>
              </div>
              <p className="md:col-span-5 text-base md:text-lg font-light leading-relaxed text-anthracite/70">
                {s.intro}
              </p>
            </div>
          </Reveal>
        )}

        <div
          className={`grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3 ${
            withHeading ? "mt-14 md:mt-20" : ""
          }`}
        >
          {serviceKeys.map((id, i) => (
            <Reveal key={id} delay={0.06 * i} className="h-full">
              <ServiceCard
                id={id}
                index={i}
                data={s.items[id]}
                image={images[id]}
                labels={s}
                detailed={detailed}
              />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.12}>
          <div className="mt-14 md:mt-20 flex flex-col md:flex-row md:items-center gap-8 md:gap-12 border-t border-sand/70 pt-10">
            <p className="flex-1 font-serif italic text-lg md:text-xl text-anthracite/70 leading-snug">
              {s.note}
            </p>
            {!detailed && (
              <Link
                to="/servizi"
                data-testid="servizi-page-link"
                className="btn-primary self-start md:self-auto whitespace-nowrap"
              >
                {s.ctaAll}
              </Link>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ServiziSection;
